import gsap from 'gsap';
import { GameScene } from '@Core/GameScene/GameScene';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ESpinParametersType, ETurboModeLevels, ISpinParameters } from '@GFTypes/GameStoreTypes';
import EventsCenter, { IEventData } from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { getCurrentTurboLevel } from '@Managers/TurboManager/TurboManager';
import { EAnimationStates, ReelAnimation } from './ReelAnimation';
import ReelSpinningSimulationBody from './ReelSpinningSimulationBody';

export class ReelSpinningSimulation {
    private readonly scene: GameScene;
    private readonly gameStore: GameStore;
    private readonly reelIndex: number;
    private readonly symbolsPerReel: number;
    private readonly symbolsPerReelWithTopAndBottom: number;

    public finalSymbolsRevealed: number;
    private switchingPoint: number = 0;
    private symbolsShownAfterResponse: number;
    private startTime: number;
    private symbolSwitchesSinceReceivedResponse: number = 0;
    private timeSinceReceivedResponse: number = 0;
    private timeSinceStartedTension: number = 0;
    private timeSinceStartedAfterTension: number = 0;

    private minimalSpinDuration: number;
    private startYOffsTime: number;
    private startYOffStopY: number;
    private stopYOffsetTime: number;
    private stopYOffset: number;

    private desiredVelocity: number;
    private desiredTensionVelocity: number;
    private desiredQuickStopVelocity: number;
    private accelerationTime: number;
    private decelerationDistance: number;
    private afterTensionDuration: number;
    private tensionDuration: number;
    private finalSymbolDistance: number;
    private fullTensionDuration: number;
    private reelTensionStarted: boolean = false;
    private afterTensionStart: boolean = false;
    private responseReceived: boolean = false;
    private minimalSpinDurationReached: boolean = false;

    public isQuickStopRequested: boolean = false;
    public reelHasTension: boolean = false;
    public reelHasFullTension: boolean = false;
    public reelAfterTension: boolean = false;
    public soundPlayed: boolean = false;

    public simulationBodies: ReelSpinningSimulationBody[] = [];
    private animationState: EAnimationStates;
    private reelAnimation: ReelAnimation;
    public onUpdateFunction: (delta: number) => void = () => {};

    constructor(scene: GameScene, reelIndex: number, reelAnimation: ReelAnimation, bodyIds: number[]) {
        this.scene = scene;
        this.gameStore = this.scene.game.gameStore;
        this.reelAnimation = reelAnimation;
        this.reelIndex = reelIndex;
        this.symbolsPerReel = this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        this.symbolsPerReelWithTopAndBottom = this.symbolsPerReel + 2;
        this.finalSymbolsRevealed = 0;

        this.setSpinningParametersSet();
        this.setSpinningParameters(this.gameStore.get(EGameStoreKeys.SpinParameters));

        this.createSimulationBodies(bodyIds);
        this.switchingPoint = this.symbolsPerReelWithTopAndBottom;

        this.animationState = EAnimationStates.CREATE_STATE;

        EventsCenter.on(EGameEvents.TENSION_START_$, this.handleTensionStart, this);
        EventsCenter.on(EGameEvents.TENSION_INTRO_$, this.handleTensionStart, this);
        EventsCenter.on(EGameEvents.AFTER_TENSION_START_$, this.onAfterTensionStart, this);
        EventsCenter.on(EGameEvents.TENSION_WIN_$, this.onTensionEnd, this);
        EventsCenter.on(EGameEvents.TENSION_LOSE_$, this.handleTensionEnd, this);
    }

    private handleTensionStart(data: IEventData) {
        this.onTensionStart(data.eventIds![0], data.tensionedReels);
    }

    private handleTensionEnd({ reelIndex, tensionedReels }: { reelIndex: number; tensionedReels: boolean[] }) {
        this.onTensionEnd(reelIndex, tensionedReels);
    }

    private createSimulationBodies(bodyIds: number[]): void {
        for (let i = 0; i < this.symbolsPerReelWithTopAndBottom; i++) {
            this.simulationBodies.push(new ReelSpinningSimulationBody(this.scene, { x: this.scene.screen.width / 2, y: i }, bodyIds[i]));
        }
    }

    private setSpinningParameters(spinParameters: ISpinParameters): void {
        const {
            symbolsShownAfterResponsePerReel,
            minimalSpinDuration,
            startYOffset,
            startYOffsetTime,
            stopYOffsetTime,
            stopYOffset,
            decelerateFrom,
            desiredVelocity,
            accelerationTime,
            tensionVelocityModifier,
            quickStopVelocityModifier,
            fullTensionDuration
        } = spinParameters;
        const lastReelSpinDuration = symbolsShownAfterResponsePerReel[symbolsShownAfterResponsePerReel.length - 1];

        this.minimalSpinDuration = minimalSpinDuration - lastReelSpinDuration;
        this.symbolsShownAfterResponse = symbolsShownAfterResponsePerReel[this.reelIndex];
        this.desiredVelocity = desiredVelocity * this.symbolsPerReel;
        this.desiredTensionVelocity = this.desiredVelocity * tensionVelocityModifier;
        this.desiredQuickStopVelocity = this.desiredVelocity * quickStopVelocityModifier;
        this.accelerationTime = accelerationTime / 1000;
        this.startYOffsTime = startYOffsetTime;
        this.startYOffStopY = -startYOffset * this.symbolsPerReel;
        this.stopYOffsetTime = stopYOffsetTime;
        this.stopYOffset = stopYOffset * this.symbolsPerReel;
        this.decelerationDistance = Math.max(0, decelerateFrom * this.symbolsPerReel);
        this.fullTensionDuration = fullTensionDuration;
        this.finalSymbolDistance = this.symbolsPerReelWithTopAndBottom + this.stopYOffset;
        if (this.decelerationDistance > this.finalSymbolDistance) {
            this.decelerationDistance = this.finalSymbolDistance;
            console.warn('Deceleration distance is greater then available distance.');
        }
    }

    private setSpinningParametersSet(): void {
        const paramsSets = this.gameStore.get(EGameStoreKeys.SpinParametersSets);
        const turboMode = this.gameStore.get(EGameStoreKeys.TurboModeState);
        const forceSpinningSet = this.gameStore.get(EGameStoreKeys.EnvironmentConfiguration).forceSpinningSet;
        let paramsSet: ISpinParameters | undefined;

        if (turboMode.active) {
            const turboModeLevel = getCurrentTurboLevel(this.gameStore);
            if (turboModeLevel === ETurboModeLevels.FAST) paramsSet = paramsSets.find((_paramsSet) => _paramsSet.type === ESpinParametersType.FAST);
            if (turboModeLevel === ETurboModeLevels.TURBO) paramsSet = paramsSets.find((_paramsSet) => _paramsSet.type === ESpinParametersType.TURBO);
        }

        if (forceSpinningSet) paramsSet = paramsSets.find((_paramsSet) => _paramsSet.type === forceSpinningSet);

        paramsSet ??= paramsSets.find((_paramsSet) => _paramsSet.type === ESpinParametersType.NORMAL)!;

        this.gameStore.set(EGameStoreKeys.SpinParameters, paramsSet);
    }

    private getRemainingStoppingDistance(): number {
        const pendingFinalSymbols = this.symbolsPerReelWithTopAndBottom - this.finalSymbolsRevealed;
        return pendingFinalSymbols + this.stopYOffset - this.sortedBodies()[0].positionY;
    }

    private reset(): void {
        this.finalSymbolsRevealed = 0;
        this.symbolSwitchesSinceReceivedResponse = 0;
        this.timeSinceReceivedResponse = 0;
        this.timeSinceStartedTension = 0;
        this.timeSinceStartedAfterTension = 0;
        this.responseReceived = false;
        this.minimalSpinDurationReached = false;
        for (const body of this.simulationBodies) {
            body.setAcceleration(0).setVelocity(0);
        }
        this.isQuickStopRequested = false;
        this.setSpinningParametersSet();
        this.setSpinningParameters(this.gameStore.get(EGameStoreKeys.SpinParameters));
    }

    public sortedBodies(): ReelSpinningSimulationBody[] {
        return this.simulationBodies.toSorted((symbolA, symbolB) => symbolA.positionY - symbolB.positionY);
    }

    public animate(): void {
        this.reset();
        this.animateStartingOffset();
    }

    private animateStartingOffset(): void {
        EventsCenter.emit(EGameEvents.REEL_SPIN_START_$, { eventIds: [this.reelIndex + 1] });

        if (this.startYOffStopY === 0) {
            this.startAccelerationPhase();
            return;
        }

        this.sortedBodies().forEach((body, index) => {
            gsap.to(body, {
                duration: this.startYOffsTime / 1000,
                ease: 'power2.in',
                y: body.positionY + this.startYOffStopY,
                onComplete: () => {
                    if (index === this.symbolsPerReelWithTopAndBottom - 1) {
                        this.startAccelerationPhase();
                    }
                }
            });
        });
    }

    private startAccelerationPhase(): void {
        this.sortedBodies().forEach((body) => {
            body.setMaxVelocity(this.desiredVelocity);
            body.setAcceleration(this.desiredVelocity / this.accelerationTime);
        });
        this.startTime = performance.now();
        this.animationState = EAnimationStates.ACC_STATE;
        const turboState = this.gameStore.get(EGameStoreKeys.TurboModeState);
        const isTurbo = turboState.active && getCurrentTurboLevel(this.gameStore) === ETurboModeLevels.TURBO;
        if (isTurbo) this.quickStop();
        this.onUpdateFunction = this.updateAccelerationPhase;
    }

    private updateAccelerationPhase(delta: number): void {
        const spinningTime = performance.now() - this.startTime;
        const bodies = this.sortedBodies();
        const lastBody = bodies.at(-1)!;
        const canSwitchlastBody = lastBody.positionY >= this.switchingPoint;
        const IsTakeCompleted = this.gameStore.get(EGameStoreKeys.IsTakeCompleted);

        if (this.reelHasTension && this.reelTensionStarted) this.timeSinceStartedTension += delta;
        if (this.reelAfterTension && this.afterTensionStart) this.timeSinceStartedAfterTension += delta;

        if (!canSwitchlastBody) return;

        this.moveLastBodyToBack(false);

        if (spinningTime > this.minimalSpinDuration && this.responseReceived && IsTakeCompleted) {
            this.minimalSpinDurationReached = true;
            this.timeSinceReceivedResponse += delta;
            this.symbolSwitchesSinceReceivedResponse++;

            const canFinishNormalSpin = this.symbolSwitchesSinceReceivedResponse >= this.symbolsShownAfterResponse;
            const canFinishByQS = spinningTime > this.minimalSpinDuration && this.isQuickStopRequested;
            const tensionCanDecelerate = this.timeSinceStartedTension >= this.tensionDuration;
            const afterTensionFinished = this.timeSinceStartedAfterTension >= this.afterTensionDuration;
            const canFinishFullTension = this.timeSinceReceivedResponse >= this.fullTensionDuration + this.symbolsShownAfterResponse;

            let canStartDecelerating = false;

            if (this.reelHasTension) {
                canStartDecelerating = (canFinishNormalSpin && tensionCanDecelerate) || canFinishByQS;
            } else if (this.reelAfterTension) {
                canStartDecelerating = (canFinishNormalSpin && afterTensionFinished) || canFinishByQS;
            } else if (this.reelHasFullTension) {
                canStartDecelerating = canFinishNormalSpin && canFinishFullTension;
            } else {
                canStartDecelerating = canFinishNormalSpin || canFinishByQS;
            }

            if (canStartDecelerating) {
                while (this.sortedBodies().at(-1)!.positionY >= this.switchingPoint) {
                    this.moveLastBodyToBack(false);
                }
                this.startDecelerationPhase();
            }
        }
    }

    private startDecelerationPhase(): void {
        const bodies = this.sortedBodies();
        const remainingDistance = this.getRemainingStoppingDistance();
        const brakingDistance = Math.min(this.decelerationDistance, remainingDistance);
        const velocity = bodies[0].velocity;

        for (const body of bodies) {
            body.stopAfterDistance(remainingDistance, brakingDistance, velocity);
        }

        this.startTime = performance.now();
        this.onUpdateFunction = this.updateDecelerationPhase;
        this.animationState = EAnimationStates.DEC_STATE;
    }

    private updateDecelerationPhase(_delta: number): void {
        if (this.isQuickStopRequested) return this.quickStop();

        const finished = this.simulationBodies.every((body) => body.hasFinishedStopping());
        const tolerance = finished ? 1e-9 : 0;

        while (this.finalSymbolsRevealed < this.symbolsPerReelWithTopAndBottom && this.sortedBodies().at(-1)!.positionY >= this.switchingPoint - tolerance) {
            this.moveLastBodyToBack(true);
        }

        if (finished) {
            this.onUpdateFunction = () => {};
            this.animateBouncing();
        }
    }

    private animateBouncing(): void {
        this.animationState = EAnimationStates.BOUNCING_STATE;

        if (this.stopYOffset === 0) {
            this.endAnimation();
            return;
        }

        this.sortedBodies().forEach((body, index) => {
            const startY = body.positionY;
            const tweenState = { progress: 0 };

            gsap.to(tweenState, {
                progress: 1,
                duration: this.stopYOffsetTime / 1000,
                ease: 'power3.out',
                onUpdate: () => {
                    body.positionY = gsap.utils.interpolate(startY, index, tweenState.progress);
                    this.reelAnimation.syncSymbolsWithSimulation();
                },
                onComplete: () => {
                    body.positionY = index;
                    this.reelAnimation.syncSymbolsWithSimulation();

                    if (index === this.symbolsPerReelWithTopAndBottom - 1) {
                        this.endAnimation();
                    }
                }
            });
        });
    }

    private endAnimation(): void {
        this.animationState = EAnimationStates.END_STATE;
        EventsCenter.emit(EGameEvents.PRE_REEL_SPIN_END_$, { eventIds: [this.reelIndex + 1] });
    }

    private moveLastBodyToBack(final: boolean = false): void {
        const firstBody = this.sortedBodies().at(0)!;
        const lastBody = this.sortedBodies().at(-1)!;

        lastBody.positionY = firstBody.positionY - 1;

        this.reelAnimation.switchToNextSymbol(lastBody.id, firstBody.id, final);

        if (final) this.finalSymbolsRevealed++;
    }

    public getAnimationState(): EAnimationStates {
        return this.animationState;
    }

    public quickStop(): void {
        const canQuickStopNormalSpin = this.responseReceived && this.gameStore.get(EGameStoreKeys.IsTakeCompleted) && this.minimalSpinDurationReached;
        const canQuickStopTension = this.scene.game.reelTensionManager.canQuickStopTension();
        const { skipTensionPerReel } = this.gameStore.get(EGameStoreKeys.SpinParameters);
        const spinTensionStarted = this.scene.game.reelTensionManager.hasTensionStarted();
        if (!canQuickStopNormalSpin) return this.setQuickStopBeforeCanFinish();

        if (this.reelHasTension) {
            if (!canQuickStopTension) return;

            if (skipTensionPerReel && this.reelTensionStarted) {
                this.setQuickStopFinish();
            }

            if (!skipTensionPerReel && spinTensionStarted) {
                this.setQuickStopFinish(true);
            }

            if (!spinTensionStarted) this.setQuickStopFinish(true);
        } else this.setQuickStopFinish();
    }

    private setQuickStopBeforeCanFinish(): void {
        this.desiredVelocity = this.desiredQuickStopVelocity;
        this.applyDesiredVelocityToSymbols();
        this.isQuickStopRequested = true;
        EventsCenter.emit(EGameEvents.INTERRUPT_TENSION);
    }

    private setQuickStopFinish(interruptAnimation?: boolean): void {
        if (this.getAnimationState() === EAnimationStates.DEC_STATE) {
            this.stopSymbolsAtDestination();
            this.onUpdateFunction = () => {};
            this.animateBouncing();
        } else if (this.getAnimationState() === EAnimationStates.ACC_STATE) {
            this.applyDesiredVelocityToSymbols();
        }

        if (interruptAnimation) EventsCenter.emit(EGameEvents.INTERRUPT_TENSION);
        this.isQuickStopRequested = true;
    }

    private stopSymbolsAtDestination(): void {
        const bodies = this.sortedBodies();
        this.finalSymbolsRevealed = this.symbolsPerReelWithTopAndBottom - 1;
        for (const [index, body] of bodies.entries()) {
            body.setAcceleration(0);
            body.setVelocity(0);
            body.positionY = index + this.stopYOffset;
            this.reelAnimation.switchToNextSymbol(body.id, 0, true);
            this.finalSymbolsRevealed--;
        }
        this.finalSymbolsRevealed = this.symbolsPerReelWithTopAndBottom - 1;
    }

    private applyDesiredVelocityToSymbols(): void {
        this.sortedBodies().forEach((body, index) => {
            body.setVelocity(this.desiredVelocity);
            body.setMaxVelocity(this.desiredVelocity);
        });
    }

    public passSpinResponse(): void {
        this.responseReceived = true;
        const tensionManager = this.scene.game.reelTensionManager;
        this.reelHasFullTension = tensionManager.isFullTensionPresent();

        if (!tensionManager.isSpinWithTension() || this.reelHasFullTension) return;

        this.reelHasTension = tensionManager.reelHasTension(this.reelIndex);

        if (this.reelHasTension) {
            this.tensionDuration = tensionManager.getReelTensionDuration(this.reelIndex);
        }

        this.reelAfterTension = tensionManager.isReelAfterLastTensionedReel(this.reelIndex);

        if (this.reelAfterTension) {
            this.afterTensionDuration = tensionManager.getAfterReelTensionDuration(this.reelIndex);
        }
    }

    private onTensionStart(startingIndex: number, tensionedReels: boolean[]): void {
        if (tensionedReels[this.reelIndex]) this.desiredVelocity = this.desiredTensionVelocity;
        if (this.reelIndex === startingIndex - 1) this.reelTensionStarted = true;
    }

    private onAfterTensionStart(): void {
        if (this.reelAfterTension) this.afterTensionStart = true;
    }

    private onTensionEnd(finishingIndex: number, tensionedReels: boolean[]): void {
        if (this.reelIndex > finishingIndex - 1) {
            this.afterTensionStart = true;
        }
    }

    public destroy(): void {
        this.simulationBodies = [];
        EventsCenter.off(EGameEvents.TENSION_START_$, this.handleTensionStart, this);
        EventsCenter.off(EGameEvents.TENSION_INTRO_$, this.handleTensionStart, this);
        EventsCenter.off(EGameEvents.AFTER_TENSION_START_$, this.onAfterTensionStart, this);
        EventsCenter.off(EGameEvents.TENSION_WIN_$, this.onTensionEnd, this);
        EventsCenter.off(EGameEvents.TENSION_LOSE_$, this.handleTensionEnd, this);
    }
}
